import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  jsonLd?: object;
}

const SCRIPT_ID = 'seo-jsonld';

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalPath,
  jsonLd,
}: SeoHeadProps) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';
  const canonicalUrl = `${siteUrl}${canonicalPath}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (
      selector: string,
      content: string,
      attrName: string = 'name',
    ) => {
      let el = document.querySelector<HTMLMetaElement>(
        `meta[${attrName}="${selector}"]`,
      );
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, selector);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:title', title, 'name');
    setMeta('twitter:description', description, 'name');

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    if (jsonLd) {
      let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      const script = document.getElementById(SCRIPT_ID);
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalUrl, jsonLd]);

  return null;
}
